import { useRef, useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { useAuth } from '../auth/useAuth';
import { Composer, type ComposerHandle } from '../composer/Composer';
import { useCapability } from '../workspace/useCapability';

/**
 * Whether the signed-in person may edit or delete something written by
 * `authorId`: their own words, always; anyone's, if they administer the
 * workspace. The server checks the same thing against the row — this only
 * decides which buttons to draw.
 */
export function useMayChange(authorId: string | null | undefined): boolean {
  const userId = useAuth().user?.id ?? null;
  const isAdmin = useCapability('workspace.admin');
  return isAdmin || (userId !== null && authorId === userId);
}

/** "(edited)", beside the timestamp, the way chat apps say it. */
export function EditedMark({ at }: { at: string | null | undefined }) {
  if (at === null || at === undefined) return null;
  return (
    <span className="fs-11 text-faint" title={`Edited ${new Date(at).toLocaleString()}`}>
      (edited)
    </span>
  );
}

/**
 * Edit and Delete, shown on hover (and always to a keyboard user who tabs
 * onto them). The parent carries the `group` class.
 */
export function MessageActions({
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  deleteLabel = 'Delete',
}: {
  canEdit: boolean;
  canDelete: boolean;
  onEdit: () => void;
  onDelete: () => void;
  deleteLabel?: string;
}) {
  if (!canEdit && !canDelete) return null;
  return (
    <span className="ml-auto flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100">
      {canEdit && (
        <button
          type="button"
          onClick={onEdit}
          aria-label="Edit"
          title="Edit"
          className="rounded-md p-1 text-muted hover:bg-surface-3 hover:text-ink"
        >
          <Pencil size={12} strokeWidth={2} aria-hidden />
        </button>
      )}
      {canDelete && (
        <button
          type="button"
          onClick={onDelete}
          aria-label={deleteLabel}
          title={deleteLabel}
          className="rounded-md p-1 text-muted hover:bg-danger-tint hover:text-danger"
        >
          <Trash2 size={12} strokeWidth={2} aria-hidden />
        </button>
      )}
    </span>
  );
}

/** "Delete this…? Delete / Cancel", in place, instead of a dialog. */
export function ConfirmDelete({
  prompt,
  pending,
  error,
  onConfirm,
  onCancel,
}: {
  prompt: string;
  pending: boolean;
  error: Error | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-2 rounded-lg bg-danger-tint px-2.5 py-1.5 fs-12">
      <span className="flex-1 text-danger">{error !== null ? error.message : prompt}</span>
      <button type="button" onClick={onCancel} className="rounded-md px-2 py-1 text-ink-soft hover:text-ink">
        Cancel
      </button>
      <button
        type="button"
        onClick={onConfirm}
        disabled={pending}
        className="rounded-md bg-danger px-2.5 py-1 font-semibold text-white disabled:opacity-50"
      >
        {pending ? 'Deleting…' : 'Delete'}
      </button>
    </div>
  );
}

/** The message, back in the editor it was written in. */
export function MessageEditor({
  workspaceId,
  initial,
  pending,
  error,
  onSave,
  onCancel,
}: {
  workspaceId: string;
  initial: unknown;
  pending: boolean;
  error: Error | null;
  onSave: (body: unknown) => void;
  onCancel: () => void;
}) {
  const editorRef = useRef<ComposerHandle>(null);
  const [empty, setEmpty] = useState(false);
  const save = () => {
    const body = editorRef.current?.getContent();
    if (body === undefined || body === null) return;
    onSave(body);
  };

  return (
    <div className="mt-1 rounded-lg border border-line-strong p-2">
      <Composer
        ref={editorRef}
        workspaceId={workspaceId}
        ariaLabel="Edit message"
        placeholder="Write the message"
        initialContent={initial}
        onSubmit={save}
        onEmptyChange={setEmpty}
        // An edit changes the words, not the files — attachments stay as they
        // were posted, so a pasted file is simply not taken.
        onPasteFiles={() => false}
      />
      <div className="mt-1.5 flex items-center gap-2">
        {error !== null && <span className="flex-1 truncate fs-11 text-danger">{error.message}</span>}
        <button
          type="button"
          onClick={onCancel}
          className="ml-auto rounded-control border border-line bg-base px-2.5 py-1 fs-12"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={save}
          disabled={pending || empty}
          className="rounded-control bg-ink px-2.5 py-1 fs-12 font-semibold text-white disabled:opacity-50"
        >
          {pending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
